/**
 * TemplatesPage — Plantillas de documentos de venta.
 * Lista, crea, edita y elimina plantillas reutilizables.
 */
import { useState, useEffect, useRef } from 'react'
import { useCierreAlClicAfuera } from '@/lib/useCierreAlClicAfuera'
import { useNavigate } from 'react-router-dom'
import {
  Plus, MoreVertical, Loader2, FileText, Trash2, Eye, Search, Copy,
} from 'lucide-react'
import {
  useDocumentTemplates,
  useDeleteDocumentTemplate,
  useCreateDocumentFromTemplate,
  useCreateDocumentTemplate,
  useUpdateDocumentTemplate,
  useCustomers,
  type DocumentTemplate,
} from '@/modules/sales/hooks/useSales'
import { useLocalizationConfig } from '@/modules/config/hooks/useConfig'
import { ConfirmModal, useConfirmModal } from '@/components/ConfirmModal'
import { EmptyState } from '@/components/EmptyState'
import { TableSkeleton } from '@/components/Skeleton'
import { PageHeader } from '@/components/HelpPanel'
import { HELP } from '@/data/helpContent'
import { TemplateModal } from './TemplateModal'
import { DataTable, type DataTableColumn } from '@/components/ds/DataTable'
import { Pagination } from '@/components/ds/Pagination'
import { useTenantLocale } from '@/lib/locale'

// ─── Helpers ──────────────────────────────────────────────────────────────────

function DocTypeBadge({ type }: { type: string }) {
  const colors: Record<string, string> = {
    FE: 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300',
    BE: 'bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300',
    NC: 'bg-orange-50 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300',
    ND: 'bg-rose-50 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300',
    FC: 'bg-teal-50 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300',
  }
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold font-mono ${colors[type] || 'bg-surface-muted text-content-muted'}`}>
      {type}
    </span>
  )
}

const PAYMENT_LABELS: Record<string, string> = {
  contado: 'Contado',
  credito: 'Crédito',
}

// ─── TemplateRow ─────────────────────────────────────────────────────────────

/**
 * Las columnas del listado de plantillas.
 *
 * 🔴 Era `TemplateRow`, el tercero de los «componente-fila con hook». Su hook
 * es un `useState` del menú, o sea vive **en la celda de acciones**; las cuatro
 * columnas de datos son render puro.
 *
 * ⚠️ **Sin `sortable`**: esta pantalla la pagina el servidor (`page` + `pages`),
 * y ordenar en el cliente alcanzaría solo a la página visible.
 */
function columnasDePlantilla({ total, onEdit, onCreateDocument, onDelete }: {
  total: number
  onEdit: (t: DocumentTemplate) => void
  onCreateDocument: (t: DocumentTemplate) => void
  onDelete: (t: DocumentTemplate) => void
}): DataTableColumn<DocumentTemplate>[] {
  return [
    { key: 'name', header: 'Nombre',
      render: tpl => (
        <>
          <p className="text-sm font-medium text-content">{tpl.name}</p>
          {tpl.description && (
            <p className="text-xs text-content-subtle truncate max-w-xs">{tpl.description}</p>
          )}
        </>
      ) },
    { key: 'document_type', header: 'Tipo',
      render: tpl => <DocTypeBadge type={tpl.document_type} /> },
    { key: 'customer_name', header: 'Cliente',
      render: tpl => <p className="text-sm text-content">{tpl.customer_name || '—'}</p> },
    { key: 'payment_method', header: 'Forma de Pago',
      render: tpl => (
        <p className="text-sm text-content">
          {tpl.payment_method_display || PAYMENT_LABELS[tpl.payment_method] || tpl.payment_method}
        </p>
      ) },
    { key: 'acciones', header: <span className="sr-only">Acciones</span>, width: 56,
      // ⚠️ `index` no es decorativo: las tres últimas filas abren su menú hacia
      // ARRIBA, o quedaría fuera del panel. Por eso el `render` lo recibe.
      render: (tpl, index) => (
        <AccionesDePlantilla
          tpl={tpl}
          haciaArriba={total > 3 && index >= total - 3}
          onEdit={onEdit}
          onCreateDocument={onCreateDocument}
          onDelete={onDelete}
        />
      ) },
  ]
}

/** La celda de acciones: el menú y su estado abierto/cerrado. */
function AccionesDePlantilla({
  tpl,
  onEdit,
  onCreateDocument,
  onDelete,
  haciaArriba,
}: {
  tpl: DocumentTemplate
  onEdit: (t: DocumentTemplate) => void
  onCreateDocument: (t: DocumentTemplate) => void
  onDelete: (t: DocumentTemplate) => void
  haciaArriba: boolean
}) {
  const [menuOpen, setMenuOpen] = useState(false)
  const disparadorRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  useCierreAlClicAfuera(menuOpen, [disparadorRef, panelRef], () => setMenuOpen(false))
  const isLast = haciaArriba

  return (
    <div className="text-right relative">
        <button ref={disparadorRef}
          onClick={() => setMenuOpen(p => !p)}
          className="p-1.5 rounded-lg hover:bg-soft text-content-subtle transition-all"
        >
          <MoreVertical size={15} />
        </button>

        {menuOpen && (
          <>
            <div ref={panelRef}
              className={`absolute right-4 z-50 bg-popover border border-divider rounded-xl shadow-lg py-1 min-w-[170px]
              ${isLast ? 'bottom-10' : 'top-10'}`}
              style={{ backgroundColor: 'rgb(var(--color-popover))' }}
            >
              <button
                onClick={() => { onEdit(tpl); setMenuOpen(false) }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-content hover:bg-soft transition-colors"
              >
                <Eye size={14} />Editar
              </button>
              <button
                onClick={() => { onCreateDocument(tpl); setMenuOpen(false) }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-primary hover:bg-primary-50 transition-colors"
              >
                <Copy size={14} />Crear Documento
              </button>
              <button
                onClick={() => { onDelete(tpl); setMenuOpen(false) }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-danger-content-soft hover:bg-danger-surface transition-colors"
              >
                <Trash2 size={14} />Eliminar
              </button>
            </div>
          </>
        )}
    </div>
  )
}

// ─── TemplatesPage ───────────────────────────────────────────────────────────

export function TemplatesPage() {
  const navigate = useNavigate()

  const [searchInput, setSearchInput] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const locale = useTenantLocale()
  const [page, setPage] = useState(1)
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<Partial<DocumentTemplate> | null>(null)

  // Debounce search (400ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(searchInput)
      setPage(1)
    }, 400)
    return () => clearTimeout(timer)
  }, [searchInput])

  const { data, isLoading } = useDocumentTemplates({
    search: searchQuery || undefined,
    page,
    page_size: 20,
  })
  const { data: customersData } = useCustomers({ active: 'true', page_size: 9999 })
  const { data: locConfig } = useLocalizationConfig()

  const { mutate: createTemplate, isPending: isCreating } = useCreateDocumentTemplate()
  const updateMutation = useUpdateDocumentTemplate(editing?.id || '')
  const { mutate: deleteTemplate } = useDeleteDocumentTemplate()
  const { mutate: createFromTemplate } = useCreateDocumentFromTemplate()
  const { confirm: confirmAction, modalProps } = useConfirmModal()

  const customers = customersData?.customers || []
  const templates = data?.templates || []
  const total = data?.count || 0
  const pages = data?.pages || 1

  const handleCreate = () => {
    setEditing(null)
    setModalOpen(true)
  }

  const handleEdit = (tpl: DocumentTemplate) => {
    setEditing(tpl)
    setModalOpen(true)
  }

  const handleDelete = (tpl: DocumentTemplate) => {
    confirmAction({
      message: `¿Eliminar la plantilla "${tpl.name}"? Esta acción no se puede deshacer.`,
      variant: 'danger',
      onConfirm: () => deleteTemplate(tpl.id),
    })
  }

  const handleCreateDocument = (tpl: DocumentTemplate) => {
    createFromTemplate(
      { templateId: tpl.id },
      { onSuccess: () => navigate('/sales/documents') }
    )
  }

  const handleModalSubmit = (formData: any) => {
    if (editing?.id) {
      updateMutation.mutate(formData, { onSuccess: () => setModalOpen(false) })
    } else {
      createTemplate(formData, { onSuccess: () => setModalOpen(false) })
    }
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <PageHeader
        title="Plantillas"
        subtitle={`${total} ${total === 1 ? 'plantilla' : 'plantillas'} registradas`}
        help={HELP['sales.templates']}
      >
        <button
          onClick={handleCreate}
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-on-primary text-sm font-medium rounded-lg hover:bg-primary-hover transition-colors"
        >
          <Plus size={16} />Nueva Plantilla
        </button>
      </PageHeader>

      {/* Buscador */}
      <div className="flex items-center gap-3 mb-5 flex-wrap">
        <div className="relative ml-auto">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-content-subtle pointer-events-none" />
          <input aria-label="Buscar por nombre"
            type="text"
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            placeholder="Buscar por nombre…"
            className="pl-8 pr-3 py-2 text-sm border border-input-border rounded-lg bg-input-bg text-content focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary-200 w-64"
          />
        </div>
      </div>

      {/* Tabla */}
      <div className="bg-surface rounded-xl border border-divider overflow-visible">
        {isLoading ? (
          <TableSkeleton rows={6} cols={5} />
        ) : templates.length === 0 ? (
          <EmptyState
            icon={FileText}
            title="No hay plantillas"
            hint={searchQuery
              ? 'Prueba con otros términos de búsqueda.'
              : 'Crea la primera plantilla con el botón de arriba.'}
          />
        ) : (
          <DataTable
            columns={columnasDePlantilla({
              total: templates.length,
              onEdit: handleEdit,
              onCreateDocument: handleCreateDocument,
              onDelete: handleDelete,
            })}
            rows={templates}
            rowKey={tpl => tpl.id}
            minWidth={760}
            surface={false}
          />
        )}
      </div>

      {/* Paginación */}
      {pages > 1 && (
        <Pagination
          page={page}
          pageSize={20}
          total={total}
          onPageChange={setPage}
          locale={locale}
          className="mt-4"
        />
      )}

      {/* Modal crear/editar plantilla */}
      <TemplateModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={handleModalSubmit}
        isPending={editing?.id ? updateMutation.isPending : isCreating}
        initial={editing}
        customers={customers}
      />

      <ConfirmModal {...modalProps} />
    </div>
  )
}
